// client/src/pages/Dashboard.js

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Card from 'react-bootstrap/Card';
import Alert from 'react-bootstrap/Alert';
import Spinner from 'react-bootstrap/Spinner';
import {
  FaChartLine,
  FaRegChartBar,
  FaSteam,
  FaWallet,
  FaFileInvoiceDollar,
  FaArrowRight
} from 'react-icons/fa';
import dashboardService from '../services/dashboardService';

const eur = (v) =>
  new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(v || 0);

const usd = (v) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(v || 0);

const SHORTCUTS = [
  { to: '/bank-statements/analyse', label: 'Analyse des dépenses', icon: FaChartLine },
  { to: '/investments/overview', label: 'Investissements', icon: FaRegChartBar },
  { to: '/csgo/overview', label: 'Espace CS:GO', icon: FaSteam },
  { to: '/bank-statements/comptes', label: 'Comptes', icon: FaWallet },
  { to: '/bank-statements/import', label: 'Importer un relevé', icon: FaFileInvoiceDollar }
];

const BreakdownCard = ({ label, value, color, note }) => (
  <Card className="shadow-sm h-100 border-start border-4" style={{ borderLeftColor: color }}>
    <Card.Body>
      <div className="text-muted" style={{ fontSize: 13 }}>
        {label}
      </div>
      <div className="fw-bold" style={{ fontSize: 24, color }}>
        {value == null ? '—' : eur(value)}
      </div>
      {note && (
        <div className="text-muted" style={{ fontSize: 11 }}>
          {note}
        </div>
      )}
    </Card.Body>
  </Card>
);

function Dashboard() {
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    dashboardService
      .getOverview()
      .then((data) => {
        if (!cancelled) setOverview(data);
      })
      .catch((err) => {
        if (!cancelled) setError(err.message || 'Impossible de charger le résumé.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div>
      <h1 className="mb-4">Tableau de bord</h1>

      {error && <Alert variant="danger">{error}</Alert>}

      {loading ? (
        <Spinner animation="border" />
      ) : overview ? (
        <>
          {/* Patrimoine total */}
          <Card
            className="border-0 shadow-sm mb-4"
            style={{ backgroundColor: 'var(--color-surface-dark)' }}
          >
            <Card.Body className="py-4">
              <div
                className="text-white-50 text-uppercase fw-bold"
                style={{ fontSize: 12, letterSpacing: '0.08em' }}
              >
                Patrimoine total
              </div>
              <div
                className="fw-bold text-white tabular-nums"
                style={{ fontSize: 52, lineHeight: 1.1, fontFamily: 'var(--font-display)' }}
              >
                {eur(overview.netWorth)}
              </div>
              <div className="text-white-50" style={{ fontSize: 13 }}>
                Liquidités + PEA + crypto + capital CS:GO
              </div>
            </Card.Body>
          </Card>

          {/* Répartition */}
          <Row className="g-3 mb-4">
            <Col xs={12} md={4}>
              <BreakdownCard
                label="Liquidités"
                value={overview.totalCash}
                color="var(--color-accent)"
              />
            </Col>
            <Col xs={6} md={3}>
              <BreakdownCard
                label="PEA"
                value={overview.peaValue}
                color="#3d6a99"
                note={overview.peaValue == null ? 'indisponible' : undefined}
              />
            </Col>
            <Col xs={6} md={3}>
              <BreakdownCard
                label="Crypto"
                value={overview.cryptoValue}
                color="#9B5DE5"
                note={overview.cryptoValue == null ? 'indisponible' : undefined}
              />
            </Col>
            <Col xs={6} md={2}>
              <BreakdownCard
                label="Capital CS:GO"
                value={overview.csgoValue}
                color="var(--color-gold)"
                note={
                  overview.csgoValue == null
                    ? 'prix indisponibles'
                    : `${usd(overview.csgoValueUsd)} · 1 $ = ${Number(
                        overview.usdToEurRate || 0
                      ).toFixed(4)} €`
                }
              />
            </Col>
          </Row>

          {/* Comptes */}
          <h5 className="mb-3">Mes comptes</h5>
          <Row className="g-3 mb-4">
            {overview.accounts.length === 0 ? (
              <Col xs={12}>
                <p className="text-muted">
                  Aucun compte. Ajoutez-en un depuis la page{' '}
                  <Link to="/bank-statements/comptes">Comptes</Link>.
                </p>
              </Col>
            ) : (
              overview.accounts.map((a) => (
                <Col key={a.id} xs={6} lg={3}>
                  <Card className="border-0 shadow-sm h-100">
                    <Card.Body>
                      <div className="text-muted text-truncate" style={{ fontSize: 13 }}>
                        {a.name}
                      </div>
                      <div className="fw-bold" style={{ fontSize: 22 }}>
                        {eur(a.balance)}
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              ))
            )}
          </Row>

          {/* Raccourcis */}
          <h5 className="mb-3">Raccourcis</h5>
          <Row className="g-3">
            {SHORTCUTS.map(({ to, label, icon: Icon }) => (
              <Col key={to} xs={6} md={4} lg={3}>
                <Card
                  as={Link}
                  to={to}
                  className="border-0 shadow-sm h-100 text-decoration-none text-reset"
                >
                  <Card.Body className="d-flex align-items-center justify-content-between">
                    <span className="d-flex align-items-center gap-2">
                      <Icon size={18} className="text-primary" />
                      <span style={{ fontSize: 14 }}>{label}</span>
                    </span>
                    <FaArrowRight size={12} className="text-muted" />
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
        </>
      ) : null}
    </div>
  );
}

export default Dashboard;
